import * as React from "react";
import {useCallback} from "react";
import {useEditor, EditorContent} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {ErrorMessage, useField, useFormikContext} from "formik";
import RichTextEditorToolbar from "./RichTextEditorToolbar";
import TemplateVariablePicker from "./TemplateVariablePicker";

interface RichTextEditorFieldProps {
    name: string;
    placeholder?: string;
    showVariablePicker?: boolean;
    className?: string;
    [key: string]: any;
}

// Configure extensions at module level to ensure stable references
// and avoid duplicate extension warnings from tiptap
const configuredStarterKit = StarterKit.configure({
    heading: {levels: [1, 2, 3]},
    codeBlock: false,
    code: false,
});

const configuredLink = Link.configure({
    openOnClick: false,
    autolink: true,
    HTMLAttributes: {
        rel: "noopener noreferrer",
    },
});

const RichTextEditorField: React.FC<RichTextEditorFieldProps> = ({
    name,
    placeholder = "",
    showVariablePicker = true,
    className = "",
}) => {
    const {setFieldValue} = useFormikContext<any>();
    const [field] = useField(name);

    // Placeholder needs to be configured per-instance since it can vary
    const extensions = React.useMemo(() => [
        configuredStarterKit,
        configuredLink,
        Placeholder.configure({
            placeholder: placeholder,
        }),
    ], [placeholder]);

    const editor = useEditor({
        extensions,
        content: field.value || "",
        onUpdate: ({editor}) => {
            setFieldValue(name, editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: "prose prose-sm max-w-none focus:outline-hidden min-h-[200px] px-3 py-2",
            },
        },
        immediatelyRender: false,
    });

    const handleInsertVariable = useCallback((variable: string) => {
        if (editor) {
            editor.chain().focus().insertContent(variable).run();
        }
    }, [editor]);

    return (
        <div className={`rich-text-editor ${className}`}>
            <div className="border border-gray-300 rounded-md focus-within:ring-1 focus-within:ring-red-500 focus-within:border-red-500">
                <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-2 py-1">
                    <RichTextEditorToolbar editor={editor}/>
                    {showVariablePicker && (
                        <TemplateVariablePicker onInsert={handleInsertVariable}/>
                    )}
                </div>
                <EditorContent editor={editor}/>
            </div>
            <ErrorMessage
                name={name}
                component="p"
                className="mt-2 text-sm text-red-600"
            />
        </div>
    );
};

export default RichTextEditorField;
